import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { paddleCustomerPortalUrl } from "../integrations/paddle";
import { PageActions } from "./PageActions";

const copy = {
  en: {
    eyebrow: "Billing",
    title: "Manage your subscription and invoices.",
    intro:
      "Paddle securely handles billing for paid SailPlot branding and hosting packages.",
    cardTitle: "Open the Paddle customer portal",
    cardText:
      "Sign in with the email address used during checkout. Paddle sends a secure magic link, so no separate SailPlot account or password is required.",
    secure: "Secure access through Paddle",
    portalLabel: "Your billing in one place",
    action: "Manage subscription & invoices",
    features: [
      "View your subscription and next billing date",
      "Update your payment method",
      "View payments and download invoices",
      "Manage or cancel your subscription",
    ],
    note: "The customer portal opens on Paddle's secure website in a new tab.",
    legalNote:
      "Subscription management is separate from the statutory right of withdrawal. Mandatory cancellation or withdrawal rights and valid declarations made by another legally permitted method remain unaffected.",
    cancel: "Cancel contract",
    withdraw: "Withdraw from contract",
    declarations:
      "Submit a declaration directly to SailPlot without a login. Every declaration is stored immediately and reviewed by a person before any action is taken.",
  },
  de: {
    eyebrow: "Abrechnung",
    title: "Abonnement und Rechnungen verwalten.",
    intro:
      "Paddle übernimmt die sichere Abrechnung der kostenpflichtigen SailPlot-Branding- und Hosting-Pakete.",
    cardTitle: "Paddle-Kundenportal öffnen",
    cardText:
      "Melde dich mit der beim Kauf verwendeten E-Mail-Adresse an. Paddle sendet dir einen sicheren Magic-Link; ein separates SailPlot-Konto oder Passwort ist nicht erforderlich.",
    secure: "Sicherer Zugang über Paddle",
    portalLabel: "Deine Abrechnung an einem Ort",
    action: "Abonnement & Rechnungen verwalten",
    features: [
      "Abonnement und nächsten Abrechnungstermin ansehen",
      "Zahlungsmethode aktualisieren",
      "Zahlungen ansehen und Rechnungen herunterladen",
      "Abonnement verwalten oder kündigen",
    ],
    note: "Das Kundenportal öffnet sich in einem neuen Tab auf der sicheren Website von Paddle.",
    legalNote:
      "Die Abonnementverwaltung ist vom gesetzlichen Widerrufsrecht zu unterscheiden. Zwingende Kündigungs- oder Widerrufsrechte und Erklärungen auf einem anderen gesetzlich zulässigen Weg bleiben unberührt.",
    cancel: "Verträge hier kündigen",
    withdraw: "Vertrag widerrufen",
    declarations:
      "Reiche eine Erklärung ohne Login direkt bei SailPlot ein. Jede Erklärung wird sofort gespeichert und persönlich geprüft, bevor eine Folgemaßnahme erfolgt.",
  },
} as const;

export function ManageSubscriptionPage({
  language,
  navigate,
}: SailPlotExtensionContext) {
  const text = copy[language];

  return (
    <article className="platform-page platform-reference-page platform-subscription-page">
      <header>
        <span className="platform-page-eyebrow">{text.eyebrow}</span>
        <h1>{text.title}</h1>
        <p>{text.intro}</p>
      </header>

      <section className="platform-subscription-card">
        <header className="platform-subscription-card__intro">
          <span className="platform-subscription-secure">
            <b aria-hidden="true">✓</b>
            {text.secure}
          </span>
          <h2>{text.cardTitle}</h2>
          <p>{text.cardText}</p>
        </header>

        <ul className="platform-subscription-features">
          {text.features.map((feature) => (
            <li key={feature}>
              <span aria-hidden="true">✓</span>
              <strong>{feature}</strong>
            </li>
          ))}
        </ul>

        <div className="platform-subscription-action">
          <div>
            <strong>{text.portalLabel}</strong>
            <small>{text.note}</small>
          </div>
          <a
            className="customize-button customize-button--primary"
            href={paddleCustomerPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{text.action}</span>
            <b aria-hidden="true">↗</b>
          </a>
        </div>
      </section>

      <p className="platform-subscription-legal-note">{text.legalNote}</p>

      <section className="platform-contract-links">
        <p>{text.declarations}</p>
        <div>
          <button
            type="button"
            className="customize-button customize-button--secondary"
            onClick={() => navigate("/cancel")}
          >
            {text.cancel}
          </button>
          <button
            type="button"
            className="customize-button customize-button--secondary"
            onClick={() => navigate("/withdraw")}
          >
            {text.withdraw}
          </button>
        </div>
      </section>

      <PageActions language={language} navigate={navigate} />
    </article>
  );
}
